import React from 'react';
import { User, Building, Users, ChevronDown, Check, LucideIcon } from 'lucide-react';
import type { Role } from '../../../../shared/types/index.js';

export interface RoleOption {
  value: Role;
  label: string;
  description: string;
  icon: LucideIcon;
  colorClass: string;
}

export const roleOptions: RoleOption[] = [
  {
    value: 'penulis',
    label: 'Penulis Mandiri',
    description: 'Registrasi draft naskah & pantau statistik buku.',
    icon: User,
    colorClass: 'text-indigo-600 bg-indigo-50 border-indigo-100',
  },
  {
    value: 'penerbit',
    label: 'Penerbit Resmi',
    description: 'Manajemen katalog, verifikasi & ekspor CSV.',
    icon: Building,
    colorClass: 'text-purple-600 bg-purple-50 border-purple-100',
  },
  {
    value: 'organisasi',
    label: 'Organisasi / Komunitas',
    description: 'Penerbitan buku karya komunitas & pendaftaran langsung ke UIBN.',
    icon: Users,
    colorClass: 'text-amber-600 bg-amber-50 border-amber-100',
  },
];

interface RoleSelectorDropdownProps {
  selectedRole: Role;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onSelectRole: (role: Role) => void;
}

export const RoleSelectorDropdown: React.FC<RoleSelectorDropdownProps> = ({
  selectedRole,
  isOpen,
  onToggle,
  onClose,
  onSelectRole,
}) => {
  const selectedOption = roleOptions.find((opt) => opt.value === selectedRole) || roleOptions[0];
  const SelectedIcon = selectedOption.icon;

  return (
    <div className="relative">
      <label className="block text-xs font-semibold text-slate-700 mb-1">
        Tipe Pengguna (Hak Akses)
      </label>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-[44px] text-sm border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 bg-white font-medium text-slate-800 hover:bg-slate-50/80 transition text-left cursor-pointer"
      >
        <div className="flex items-center gap-2.5">
          <div className={`p-1 rounded-lg border shrink-0 ${selectedOption.colorClass}`}>
            <SelectedIcon className="w-4 h-4" />
          </div>
          <div>
            <span className="font-bold text-slate-900 block leading-tight">
              {selectedOption.label}
            </span>
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <>
          {/* Invisible backdrop to dismiss dropdown on click outside */}
          <div
            className="fixed inset-0 z-20 cursor-default"
            onClick={onClose}
          />
          <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-30 p-1.5 space-y-1">
            {roleOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = option.value === selectedRole;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onSelectRole(option.value);
                    onClose();
                  }}
                  className={`w-full flex items-start gap-3 p-2 rounded-lg text-left transition cursor-pointer ${
                    isSelected ? 'bg-indigo-50/80 hover:bg-indigo-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className={`p-1.5 rounded-lg border shrink-0 mt-0.5 ${option.colorClass}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-xs font-bold ${
                          isSelected ? 'text-indigo-950' : 'text-slate-800'
                        }`}
                      >
                        {option.label}
                      </span>
                      {isSelected && <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}
                    </div>
                    <p className="text-[10.5px] text-slate-500 font-medium leading-normal mt-0.5">
                      {option.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
